import React, { useCallback, useEffect, useMemo, useState } from 'react'
import apiClient, { API_ENDPOINTS, getUserInfoSync } from '../../config/api'
import { isSmallShopMode } from '../../utils/smallShopMode'

const todayISO = () => new Date().toISOString().split('T')[0]

const getPreferredBranchId = (userInfo) => {
  if (!userInfo) return null
  if (typeof userInfo.branch === 'number') return userInfo.branch
  if (typeof userInfo.branch === 'object' && userInfo.branch?.id) return userInfo.branch.id
  return (
    userInfo.branch_id ||
    userInfo.assigned_branch_id ||
    userInfo.branchId ||
    null
  )
}

const DailyReconciliation = ({ adminMode = false } = {}) => {
  const userInfo = getUserInfoSync()
  const preferredBranchId = useMemo(() => getPreferredBranchId(userInfo), [userInfo])
  const smallShopMode = isSmallShopMode()
  const showBranchSelector = adminMode && !smallShopMode

  const [branches, setBranches] = useState([])
  const [branchesLoading, setBranchesLoading] = useState(false)
  const [selectedBranchId, setSelectedBranchId] = useState('')
  const [cashiers, setCashiers] = useState([])
  const [cashierLoading, setCashierLoading] = useState(false)
  const [cashierError, setCashierError] = useState('')
  const [cashierSearch, setCashierSearch] = useState('')
  const [activeCashierId, setActiveCashierId] = useState(null)
  const [reconcileForm, setReconcileForm] = useState({
    date: todayISO(),
    amount: '',
    type_of_payment: '',
    notes: ''
  })
  const [submitting, setSubmitting] = useState(false)
  const [varianceResult, setVarianceResult] = useState(null)
  const [varianceLoading, setVarianceLoading] = useState(false)
  const [varianceError, setVarianceError] = useState('')
  const [successMessage, setSuccessMessage] = useState('')
  const [editingEntry, setEditingEntry] = useState(null)
  const [editForm, setEditForm] = useState({ total_amount: '', notes: '' })
  const [updating, setUpdating] = useState(false)

  const fetchCashiers = useCallback(async () => {
    try {
      setCashierLoading(true)
      setCashierError('')
      const response = await apiClient.get('/api/accounts/users/')
      const raw = Array.isArray(response.data) ? response.data : response.data?.results || []
      const onlyCashiers = raw
        .filter((user) => user?.user?.role === 'cashier')
        .map((user) => {
          const displayName = `${user.first_name || ''} ${user.last_name || ''}`.trim()
          const employeeName = user.user?.employee_name
          const fallbackName = user.user?.email?.split('@')?.[0]
          const branchId =
            typeof user.branch === 'number'
              ? user.branch
              : typeof user.user?.branch === 'number'
                ? user.user.branch
                : null
          const warehouseId =
            typeof user.warehouse === 'number'
              ? user.warehouse
              : typeof user.user?.warehouse === 'number'
                ? user.user.warehouse
                : null
          return {
            id: user.id || user.user?.id,
            userId: user.user?.id,
            code: user.code || '—',
            name: displayName || employeeName || fallbackName || 'Cashier',
            email: user.user?.email || '—',
            branchId,
            branchName: user.branch_name || user.user?.branch_name || '',
            warehouseId,
            warehouseName: user.warehouse_name || user.user?.warehouse_name || '',
            isActive: Boolean(user.user?.is_active)
          }
        })
      setCashiers(onlyCashiers)
    } catch (error) {
      console.error('Failed to load cashiers:', error)
      setCashiers([])
      setCashierError(error.response?.data?.detail || error.response?.data?.message || 'Unable to load cashiers. Please try again.')
    } finally {
      setCashierLoading(false)
    }
  }, [])

  const fetchBranches = useCallback(async () => {
    try {
      setBranchesLoading(true)
      const response = await apiClient.get(API_ENDPOINTS.BRANCHES)
      const branchList = response.data?.results || response.data || []
      setBranches(branchList)
      if (branchList.length === 1) {
        setSelectedBranchId(String(branchList[0].id))
      }
    } catch (error) {
      console.error('Failed to load branches:', error)
      setBranches([])
    } finally {
      setBranchesLoading(false)
    }
  }, [])

  useEffect(() => {
    fetchCashiers()
  }, [fetchCashiers])

  useEffect(() => {
    if (showBranchSelector) {
      fetchBranches()
    }
  }, [showBranchSelector, fetchBranches])

  const handleCashierSearchChange = (event) => {
    setCashierSearch(event.target.value)
  }

  const handleSelectCashier = (cashier) => {
    setActiveCashierId(cashier.userId)
    setReconcileForm({
      date: todayISO(),
      amount: '',
      type_of_payment: '',
      notes: ''
    })
    setVarianceResult(null)
    setVarianceError('')
    setSuccessMessage('')
  }

  const branchFilteredCashiers = useMemo(() => {
    if (!showBranchSelector || !selectedBranchId) {
      return cashiers
    }
    const branchId = Number(selectedBranchId)
    return cashiers.filter((cashier) => cashier.branchId === branchId)
  }, [cashiers, showBranchSelector, selectedBranchId])

  const filteredCashiers = useMemo(() => {
    const baseList = branchFilteredCashiers
    if (!cashierSearch.trim()) {
      return baseList
    }
    const query = cashierSearch.toLowerCase()
    return baseList.filter((cashier) => {
      return (
        cashier.name.toLowerCase().includes(query) ||
        cashier.code.toLowerCase().includes(query) ||
        cashier.email.toLowerCase().includes(query) ||
        cashier.branchName.toLowerCase().includes(query) ||
        cashier.warehouseName.toLowerCase().includes(query)
      )
    })
  }, [branchFilteredCashiers, cashierSearch])

  const handleBranchChange = (event) => {
    const branchId = event.target.value
    setSelectedBranchId(branchId)
    setActiveCashierId(null)
    setVarianceResult(null)
    setVarianceError('')
    setSuccessMessage('')
  }

  const getAssignmentLabel = (cashier) => {
    if (cashier.branchName) {
      return cashier.branchName
    }
    if (cashier.warehouseName) {
      return cashier.warehouseName
    }
    return 'Unassigned'
  }

  const activeCashier = useMemo(
    () => cashiers.find((cashier) => cashier.userId === activeCashierId) || null,
    [cashiers, activeCashierId]
  )

  const resolveBranchId = (cashier) => {
    if (showBranchSelector && selectedBranchId) {
      return Number(selectedBranchId)
    }
    if (!cashier) return preferredBranchId || null
    if (typeof cashier.branchId === 'number') {
      return cashier.branchId
    }
    return preferredBranchId || null
  }

  const handleFormChange = (event) => {
    const { name, value } = event.target
    setReconcileForm((prev) => ({
      ...prev,
      [name]: value
    }))
  }

  const fetchVariance = useCallback(
    async ({ cashierId, branchId, date }) => {
      if (!cashierId) return
      setVarianceLoading(true)
      setVarianceError('')
      try {
        const response = await apiClient.get(API_ENDPOINTS.SALES_CASH_VARIANCE, {
          params: {
            cashier_id: cashierId,
            branch_id: branchId || undefined,
            date
          }
        })
        setVarianceResult(response.data || null)
      } catch (error) {
        console.error('Failed to calculate variance:', error)
        setVarianceResult(null)
        setVarianceError(
          error.response?.data?.detail ||
            error.response?.data?.message ||
            'Unable to calculate variance.'
        )
      } finally {
        setVarianceLoading(false)
      }
    },
    []
  )

  const handleSubmit = async (event) => {
    event.preventDefault()
    if (!activeCashier) {
      setVarianceError('Select a cashier to reconcile.')
      return
    }
    if (!reconcileForm.amount || Number(reconcileForm.amount) <= 0) {
      setVarianceError('Enter the cash you received (amount must be greater than zero).')
      return
    }
    if (!reconcileForm.type_of_payment) {
      setVarianceError('Select the type of payment.')
      return
    }
    if (!reconcileForm.date) {
      setVarianceError('Select the date you are reconciling for.')
      return
    }
    const branchId = resolveBranchId(activeCashier)
    if (!branchId) {
      setVarianceError(
        showBranchSelector
          ? 'Select a branch before reconciling.'
          : 'No branch assigned. Please ensure the cashier or your profile has a branch.'
      )
      return
    }

    try {
      setSubmitting(true)
      setVarianceError('')
      setSuccessMessage('')

      await apiClient.post(API_ENDPOINTS.SALES_CASH_RECEIVED, {
        cashier: activeCashier.userId,
        branch: branchId,
        date: reconcileForm.date,
        total_amount: Number(reconcileForm.amount),
        type_of_payment: reconcileForm.type_of_payment,
        notes: reconcileForm.notes?.trim() || ''
      })

      setSuccessMessage('Cash received captured successfully.')
      setReconcileForm({
        date: reconcileForm.date,
        amount: '',
        type_of_payment: '',
        notes: ''
      })
      await fetchVariance({
        cashierId: activeCashier.userId,
        branchId,
        date: reconcileForm.date
      })
    } catch (error) {
      console.error('Failed to reconcile cash:', error)
      const apiError = error.response?.data
      setVarianceError(
        apiError?.detail ||
          apiError?.non_field_errors?.[0] ||
          apiError?.total_amount?.[0] ||
          'Unable to save cash received.'
      )
    } finally {
      setSubmitting(false)
    }
  }

  const handleEditEntry = (entry) => {
    setEditingEntry(entry)
    setEditForm({
      total_amount: entry.total_amount || '',
      notes: entry.notes || ''
    })
  }

  const handleCancelEdit = () => {
    setEditingEntry(null)
    setEditForm({ total_amount: '', notes: '' })
  }

  const handleUpdateEntry = async (entryId) => {
    if (!editForm.total_amount || Number(editForm.total_amount) <= 0) {
      setVarianceError('Enter a valid amount (must be greater than zero).')
      return
    }

    try {
      setUpdating(true)
      setVarianceError('')
      setSuccessMessage('')

      await apiClient.put(`${API_ENDPOINTS.SALES_CASH_RECEIVED}${entryId}/`, {
        total_amount: editForm.total_amount,
        notes: editForm.notes?.trim() || ''
      })

      setSuccessMessage('Cash received entry updated successfully.')
      setEditingEntry(null)
      setEditForm({ total_amount: '', notes: '' })
      
      await fetchVariance({
        cashierId: activeCashier.userId,
        branchId: resolveBranchId(activeCashier),
        date: reconcileForm.date
      })
    } catch (error) {
      console.error('Failed to update cash received:', error)
      const apiError = error.response?.data
      setVarianceError(
        apiError?.detail ||
          apiError?.non_field_errors?.[0] ||
          apiError?.total_amount?.[0] ||
          'Unable to update cash received entry.'
      )
    } finally {
      setUpdating(false)
    }
  }

  const canEditEntry = (entry) => {
    // Allow editing if we have variance result and entry
    if (!entry || !varianceResult) return false
    
    const today = todayISO()
    const formDate = reconcileForm.date || today
    
    // Normalize dates to YYYY-MM-DD format for comparison
    const normalizeDate = (dateStr) => {
      if (!dateStr) return null
      // Extract YYYY-MM-DD from any date string (handles "2025-12-14" or "2025-12-14T..." formats)
      if (typeof dateStr === 'string') {
        const match = dateStr.match(/^(\d{4}-\d{2}-\d{2})/)
        if (match) return match[1]
      }
      return null
    }
    
    // Check if form date is today - if so, allow editing
    if (formDate === today) {
      return true
    }
    
    // Check variance date matches form date or today
    if (varianceResult.date) {
      const varianceDate = normalizeDate(varianceResult.date)
      const normalizedFormDate = normalizeDate(formDate)
      const normalizedToday = normalizeDate(today)
      
      // Allow editing if variance date matches today OR the selected form date
      if (varianceDate && (varianceDate === normalizedToday || varianceDate === normalizedFormDate)) {
        return true
      }
    }
    
    return false
  }

  return (
    <>
      <div className="pagetitle">
        <h1>Daily Reconciliation</h1>
        <nav>
          <ol className="breadcrumb">
            <li className="breadcrumb-item">
              <a href={adminMode ? '/organisation-admin' : '/branch-manager'}>Home</a>
            </li>
            <li className="breadcrumb-item">Sales</li>
            <li className="breadcrumb-item active">Daily Reconciliation</li>
          </ol>
        </nav>
      </div>

      <section className="section">
        <div className="row gy-4">
          {showBranchSelector && (
            <div className="col-12">
              <div className="card">
                <div className="card-body">
                  <div className="row g-3 align-items-end">
                    <div className="col-md-6 col-lg-4">
                      <label className="form-label" htmlFor="reconcile-branch">
                        Branch
                      </label>
                      <select
                        id="reconcile-branch"
                        className="form-select"
                        value={selectedBranchId}
                        onChange={handleBranchChange}
                        disabled={branchesLoading}
                      >
                        <option value="">Select a branch...</option>
                        {branches.map((branch) => (
                          <option key={branch.id} value={branch.id}>
                            {branch.name}
                          </option>
                        ))}
                      </select>
                    </div>
                    <div className="col-md-6 col-lg-8">
                      <p className="text-muted small mb-0">
                        Choose the branch you want to reconcile. Cashiers listed below are filtered to that branch.
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          )}

          <div className="col-12">
            <div className="card">
              <div className="card-body">
                <div className="d-flex flex-column flex-md-row align-items-md-center justify-content-between gap-3 mb-4">
                  <div>
                    <h5 className="card-title mb-1">Cashiers</h5>
                    <p className="text-muted small mb-0">
                      {showBranchSelector && !selectedBranchId
                        ? 'Select a branch above to view its cashiers.'
                        : 'Select a cashier to capture received cash and calculate the variance.'}
                    </p>
                  </div>
                  <div className="d-flex gap-2 w-100 w-md-auto">
                    <input
                      type="search"
                      className="form-control"
                      placeholder="Search by name, code, email, or branch"
                      value={cashierSearch}
                      onChange={handleCashierSearchChange}
                    />
                    <button
                      type="button"
                      className="btn btn-outline-secondary"
                      onClick={fetchCashiers}
                      disabled={cashierLoading}
                    >
                      {cashierLoading ? (
                        <span className="spinner-border spinner-border-sm" role="status" aria-hidden="true" />
                      ) : (
                        <i className="bi bi-arrow-clockwise" />
                      )}
                    </button>
                  </div>
                </div>

                {cashierError && (
                  <div className="alert alert-danger" role="alert">
                    {cashierError}
                  </div>
                )}

                {cashierLoading ? (
                  <div className="text-center py-5">
                    <div className="spinner-border text-primary" role="status" aria-hidden="true" />
                    <p className="text-muted mt-3 mb-0">Loading cashiers…</p>
                  </div>
                ) : showBranchSelector && !selectedBranchId ? (
                  <div className="alert alert-light border mb-0" role="alert">
                    Select a branch to load cashiers for reconciliation.
                  </div>
                ) : filteredCashiers.length === 0 ? (
                  <div className="alert alert-light border mb-0" role="alert">
                    {cashierSearch
                      ? 'No cashiers match your search.'
                      : showBranchSelector
                        ? 'No cashiers found for the selected branch.'
                        : 'No cashiers found yet.'}
                  </div>
                ) : (
                  <div className="table-responsive">
                    <table className="table align-middle">
                      <thead>
                        <tr>
                          <th scope="col">Cashier</th>
                          <th scope="col">Code</th>
                          <th scope="col">Assignment</th>
                          <th scope="col">Status</th>
                          <th scope="col" className="text-end">Action</th>
                        </tr>
                      </thead>
                      <tbody>
                        {filteredCashiers.map((cashier) => (
                          <tr key={cashier.id}>
                            <td>
                              <div className="fw-semibold">{cashier.name}</div>
                              <div className="text-muted small">{cashier.email}</div>
                            </td>
                            <td>
                              <span className="badge bg-primary">
                                {cashier.code}
                              </span>
                            </td>
                            <td>{getAssignmentLabel(cashier)}</td>
                            <td>
                              <span className={`badge ${cashier.isActive ? 'bg-success' : 'bg-secondary'}`}>
                                {cashier.isActive ? 'Active' : 'Inactive'}
                              </span>
                            </td>
                            <td className="text-end">
                              <button
                                type="button"
                                className={`btn btn-sm ${activeCashierId === cashier.userId ? 'btn-primary' : 'btn-outline-primary'}`}
                                onClick={() => handleSelectCashier(cashier)}
                              >
                                {activeCashierId === cashier.userId ? 'Selected' : 'Reconcile'}
                              </button>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>
            </div>
          </div>

          {activeCashier && (
          <div className="col-12">
              <div className="card border-primary">
              <div className="card-body">
                  <div className="d-flex flex-column flex-md-row justify-content-between align-items-start align-items-md-center gap-3 mb-4">
                    <div>
                      <h5 className="card-title mb-1">Reconcile Cashier</h5>
                      <p className="mb-0">
                        <strong>{activeCashier.name}</strong> &middot; {activeCashier.email}
                      </p>
                      <small className="text-muted">
                        {getAssignmentLabel(activeCashier)} {resolveBranchId(activeCashier) ? `(Branch ID: ${resolveBranchId(activeCashier)})` : ''}
                      </small>
                </div>
                    <div className="text-muted small">
                      Capture the cash received, then we will calculate the variance automatically.
                    </div>
                  </div>

                  <form className="row g-3" onSubmit={handleSubmit}>
                    <div className="col-md-4">
                      <label className="form-label">Date</label>
                      <input
                        type="date"
                        className="form-control"
                        name="date"
                        value={reconcileForm.date}
                        max={todayISO()}
                        onChange={handleFormChange}
                      />
                  </div>
                  <div className="col-md-4">
                      <label className="form-label">Cash Received</label>
                      <div className="input-group">
                        <span className="input-group-text">$</span>
                        <input
                          type="number"
                          className="form-control"
                          name="amount"
                          min="0"
                          step="0.01"
                          placeholder="0.00"
                          value={reconcileForm.amount}
                          onChange={handleFormChange}
                        />
                      </div>
                    </div>
                    <div className="col-md-4">
                      <label className="form-label">Type of Payment</label>
                      <select
                        className="form-select"
                        name="type_of_payment"
                        value={reconcileForm.type_of_payment}
                        onChange={handleFormChange}
                        required
                      >
                        <option value="">Select payment type...</option>
                        <option value="usd_cash">USD Cash</option>
                        <option value="zig_cash">ZIG Cash</option>
                        <option value="ecocash_usd">Ecocash USD</option>
                        <option value="ecocash_zig">Ecocash ZIG</option>
                        <option value="bank_transfer_zig">Bank Transfer ZIG</option>
                        <option value="bank_transfer_usd">Bank Transfer USD</option>
                      </select>
                    </div>
                    <div className="col-12">
                      <label className="form-label">Notes (optional)</label>
                      <textarea
                        className="form-control"
                        rows="2"
                        name="notes"
                        value={reconcileForm.notes}
                        onChange={handleFormChange}
                        placeholder="Add context or reference numbers"
                      />
                  </div>
                    <div className="col-12 d-flex gap-2">
                      <button type="submit" className="btn btn-primary" disabled={submitting}>
                        {submitting ? (
                          <>
                            <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true" />
                            Saving...
                          </>
                        ) : (
                          'Reconcile Now'
                        )}
                      </button>
                      <button
                        type="button"
                        className="btn btn-outline-secondary"
                        onClick={() => fetchVariance({
                          cashierId: activeCashier.userId,
                          branchId: resolveBranchId(activeCashier),
                          date: reconcileForm.date
                        })}
                        disabled={varianceLoading}
                      >
                        {varianceLoading ? (
                          <>
                            <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true" />
                            Checking variance...
                          </>
                        ) : (
                          'Refresh Variance'
                        )}
                      </button>
                    </div>
                  </form>

                  {successMessage && (
                    <div className="alert alert-success mt-3" role="alert">
                      {successMessage}
                  </div>
                  )}

                  {varianceError && (
                    <div className="alert alert-danger mt-3" role="alert">
                      {varianceError}
                </div>
                  )}

                  {varianceResult && !varianceError && (
                    <>
                      <div className="row g-3 mt-3">
                        <div className="col-md-3">
                          <div className="border rounded-3 p-3 h-100">
                            <p className="text-uppercase text-muted small mb-1">Net Sales</p>
                            <h4 className="mb-0">${Number(varianceResult.net_sales_amount_usd || varianceResult.net_sales_amount || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</h4>
                            <small className="text-muted">Cash sales completed ({varianceResult.sales_count || 0} orders)</small>
                          </div>
                        </div>
                        <div className="col-md-3">
                          <div className="border rounded-3 p-3 h-100">
                            <p className="text-uppercase text-muted small mb-1">Cash Received</p>
                            <h4 className="mb-0">${Number(varianceResult.cash_received_amount_usd || varianceResult.cash_received_amount || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</h4>
                            <small className="text-muted">{varianceResult.has_cash_received_entries ? `${varianceResult.cash_received_entries_count || 0} entr${(varianceResult.cash_received_entries_count || 0) === 1 ? 'y' : 'ies'}` : 'No entries recorded'}</small>
                          </div>
                        </div>
                        <div className="col-md-3">
                          <div className="border rounded-3 p-3 h-100">
                            <p className="text-uppercase text-muted small mb-1">Variance</p>
                            <h4 className={`mb-0 ${Number(varianceResult.variance_usd || varianceResult.variance || 0) === 0 ? 'text-success' : 'text-danger'}`}>
                              ${Number(varianceResult.variance_usd || varianceResult.variance || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                            </h4>
                            <small className="text-muted">{Number(varianceResult.variance_percentage || 0).toFixed(2)}%</small>
                          </div>
                        </div>
                        <div className="col-md-3">
                          <div className="border rounded-3 p-3 h-100">
                            <p className="text-uppercase text-muted small mb-1">Date</p>
                            <h4 className="mb-0">{varianceResult.date}</h4>
                            <small className="text-muted">Branch: {varianceResult.branch_name || '—'}</small>
                          </div>
                        </div>
                      </div>

                      {varianceResult.cash_received_by_type && Object.keys(varianceResult.cash_received_by_type).length > 0 && (
                        <div className="mt-4">
                          <h6 className="mb-3">Cash Received by Payment Type</h6>
                          <div className="row g-3">
                            {Object.entries(varianceResult.cash_received_by_type).map(([type, data]) => {
                              const displayName = data.entries?.[0]?.type_of_payment_display || type.replace('_', ' ').replace(/\b\w/g, l => l.toUpperCase())
                              return (
                                <div key={type} className="col-md-6 col-lg-4">
                                  <div className="border rounded-3 p-3 h-100">
                                    <p className="fw-semibold mb-1">{displayName}</p>
                                    <h5 className="mb-1">
                                      ${Number(data.amount_usd || data.amount || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                                    </h5>
                                    {data.amount && data.amount_usd && Number(data.amount) !== Number(data.amount_usd) && (
                                      <small className="text-muted">
                                        Original: {Number(data.amount).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                                      </small>
                                    )}
                                    <small className="text-muted d-block mt-1">
                                      {data.entries?.length || 0} entr{(data.entries?.length || 0) === 1 ? 'y' : 'ies'}
                                    </small>
                                  </div>
                                </div>
                              )
                            })}
                          </div>
                        </div>
                      )}

                      {varianceResult.cash_received_entries && varianceResult.cash_received_entries.length > 0 && (
                        <div className="mt-4">
                          <h6 className="mb-3">Cash Received Entries</h6>
                          <div className="table-responsive">
                            <table className="table table-sm align-middle">
                              <thead>
                                <tr>
                                  <th scope="col">Type of Payment</th>
                                  <th scope="col" className="text-end">Amount</th>
                                  <th scope="col">Notes</th>
                                  <th scope="col">Entered By</th>
                                  <th scope="col">Created At</th>
                                  <th scope="col" className="text-end">Action</th>
                                </tr>
                              </thead>
                              <tbody>
                                {varianceResult.cash_received_entries.map((entry) => (
                                  <tr key={entry.id}>
                                    {editingEntry?.id === entry.id ? (
                                      <>
                                        <td>
                                          <span className="badge bg-primary">{entry.type_of_payment_display || entry.type_of_payment}</span>
                                        </td>
                                        <td className="text-end">
                                          <div className="input-group input-group-sm">
                                            <span className="input-group-text">$</span>
                                            <input
                                              type="number"
                                              className="form-control"
                                              min="0"
                                              step="0.01"
                                              value={editForm.total_amount}
                                              onChange={(e) => setEditForm(prev => ({ ...prev, total_amount: e.target.value }))}
                                              disabled={updating}
                                            />
                                          </div>
                                        </td>
                                        <td>
                                          <input
                                            type="text"
                                            className="form-control form-control-sm"
                                            value={editForm.notes}
                                            onChange={(e) => setEditForm(prev => ({ ...prev, notes: e.target.value }))}
                                            placeholder="Notes (optional)"
                                            disabled={updating}
                                          />
                                        </td>
                                        <td>
                                          <small className="text-muted">{entry.entered_by || '—'}</small>
                                        </td>
                                        <td>
                                          <small className="text-muted">
                                            {entry.created_at ? new Date(entry.created_at).toLocaleString() : '—'}
                                          </small>
                                        </td>
                                        <td className="text-end">
                                          <div className="btn-group btn-group-sm">
                                            <button
                                              type="button"
                                              className="btn btn-primary"
                                              onClick={() => handleUpdateEntry(entry.id)}
                                              disabled={updating}
                                            >
                                              {updating ? (
                                                <span className="spinner-border spinner-border-sm" role="status" aria-hidden="true" />
                                              ) : (
                                                'Save'
                                              )}
                                            </button>
                                            <button
                                              type="button"
                                              className="btn btn-outline-secondary"
                                              onClick={handleCancelEdit}
                                              disabled={updating}
                                            >
                                              Cancel
                                            </button>
                                          </div>
                                        </td>
                                      </>
                                    ) : (
                                      <>
                                        <td>
                                          <span className="badge bg-primary">{entry.type_of_payment_display || entry.type_of_payment}</span>
                                        </td>
                                        <td className="text-end">
                                          ${Number(entry.total_amount || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                                        </td>
                                        <td>
                                          {entry.notes ? (
                                            <span className="text-wrap d-inline-block" style={{ maxWidth: '200px' }}>
                                              {entry.notes}
                                            </span>
                                          ) : (
                                            <span className="text-muted small">No notes</span>
                                          )}
                                        </td>
                                        <td>
                                          <small className="text-muted">{entry.entered_by || '—'}</small>
                                        </td>
                                        <td>
                                          <small className="text-muted">
                                            {entry.created_at ? new Date(entry.created_at).toLocaleString() : '—'}
                                          </small>
                                        </td>
                                        <td className="text-end">
                                          {canEditEntry(entry) && (
                                            <button
                                              type="button"
                                              className="btn btn-sm btn-outline-primary"
                                              onClick={() => handleEditEntry(entry)}
                                            >
                                              Edit
                                            </button>
                                          )}
                                        </td>
                                      </>
                                    )}
                                  </tr>
                                ))}
                              </tbody>
                            </table>
                          </div>
                        </div>
                      )}
                    </>
                  )}
                </div>
              </div>
            </div>
          )}
        </div>
      </section>
    </>
  )
}

export default DailyReconciliation
